import React, {useState} from 'react';
import {Trans} from '@lingui/react';
import {Helmet} from 'react-helmet';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {i18n} from '@services/i18n';

import {loadingStates} from '@containers/Skillup360/Questionnaire360Container/Questionnaire360Container';
import Spinner from '@components/iOSSpinner';
import FlatButton from '../../FlatButton';
import ClosedQuestion from '../ClosedQuestion';
import OpenQuestion from '../OpenQuestion';
import LinkedQuestion from '../LinkedQuestion';

import cls from 'classnames';
import s from './Questionnaire360.css';

export default function Questionnaire360(props) {
  const {
    total,
    count,
    store,
    isSelfEval,
    options,
    evaluation,
    type,
    currentQuestion,
    currPage,
    onSave,
    onAnswerInput,
    totalPage,
    currentColumnIndex: initialColumnIndex = 0,
  } = props;

  const containerRef = React.useRef(null);

  const userId = useSelector(state => state.getIn(['user', 'data', 'id']));
  const evaluationSavingState = useSelector(state =>
    state.getIn(['evaluationQuestions', 'evaluationSavingState'])
  );
  const openQuestionsText = useSelector(state =>
    state.getIn(['evaluationQuestions', 'openQuestionsText'], {})
  );
  const jsonData = useSelector(state => {
    try {
      const stringJson = state.getIn(['tests', 'current', 'jsonData']);
      return JSON.parse(stringJson);
    } catch (error) {
      return null;
    }
  });
  const [currentColumnIndex, setCurrentColumnIndex] = useState(initialColumnIndex);

  const [question, setQuestion] = useState(() => {
    let q_state = [];
    //I'm merging the evaluation into the current
    //question object
    if (type === 'linked') {
      const storeAnswers = store[userId];
      if (storeAnswers) {
        const storeAnswersForThisQuestion = storeAnswers.filter(
          item => item.questionId === currentQuestion.get('id')
        );
        const answersMap = new Map();
        for (let answer of storeAnswersForThisQuestion) {
          answersMap.set(answer.answerId, answer.value);
        }

        const newAnswers = currentQuestion.get('answers').map(answer => {
          if (answersMap.has(answer.get('id'))) {
            return answer.set('value', answersMap.get(answer.get('id')));
          }

          return answer;
        });

        return currentQuestion.set('answers', newAnswers);
      }

      return currentQuestion;
    }

    currentQuestion.forEach(element => {
      const eva = evaluation.find(e => e.get('questionId') === element.get('id'));

      q_state.push({
        id: element.get('id'),
        isOpen: element.get('isOpen'),
        text: element.get('text'),
        value: eva?.get('value') || null,
        textValue: eva?.get('textValue') || null,
      });
    });

    return q_state;
  });
  React.useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.focus();
    }
    // Set column index from parent (Container)
    setCurrentColumnIndex(initialColumnIndex);
  }, [currPage, initialColumnIndex]);
  function handleSelectLinkedAnswer({answerId, value}) {
    const maxAnswers = question.get('max_answers');
    const answers = question.get('answers');

    // Se value è null, significa che l'utente vuole deselezionare
    if (value === null) {
      const answerIndex = answers.findIndex(answer => answer.get('id') == answerId);
      if (answerIndex > -1) {
        const newAnswers = answers.setIn([answerIndex, 'value'], null);
        setQuestion(question => question.setIn(['answers'], newAnswers));
      }
      return;
    }

    // Conta quante risposte sono già selezionate per questa colonna (escludendo quella corrente)
    let answersCountForThisColumn = 0;
    answers.forEach(answer => {
      if (answer.get('value') === value && answer.get('id') !== answerId) {
        answersCountForThisColumn++;
      }
    });

    // Se il limite è raggiunto, impedisci la selezione
    if (answersCountForThisColumn >= maxAnswers) {
      return;
    }

    // Altrimenti procedi con la selezione
    const answerIndex = answers.findIndex(answer => answer.get('id') === answerId);
    if (answerIndex > -1) {
      const newAnswers = answers.setIn([answerIndex, 'value'], value);
      setQuestion(question => question.setIn(['answers'], newAnswers));
    }
  }

  function handleSelectAnswer({questionId, value}) {
    let to_update = question.map(q => {
      if (q.id !== questionId) return q;

      q.value = value;

      return q;
    });

    setQuestion(to_update);
  }

  function handleAnswerInput({value}) {
    const [to_update] = question;
    to_update.textValue = value;
    setQuestion([{...to_update}]);
    
    // Salva il testo in Redux per la persistenza durante la navigazione
    if (onAnswerInput) {
      onAnswerInput({questionId: to_update.id, value});
    }
  }
  function getIsValid() {
    let valid = true;

    if (type === 'closed') {
      question?.forEach(q => {
        if (!q.value) valid = false;
      });
    }

    if (type === 'open') {
       const [q] = question;
      // Prendi il testo da Redux (quello che l'utente sta scrivendo adesso)
      const currentText = openQuestionsText[q.id] || '';
      // Prendi il testo originale da evaluation (quello al caricamento)
      const evaluationItem = evaluation.find(e => e.get('questionId') === q.id);
      const savedText = evaluationItem?.get('textValue') || '';

      // Se non c'è testo → BLOCCATO
      if (!currentText && !savedText) {
        valid = false;
      }
      // Se c'è testo ma sono diversi → BLOCCATO (cambiamenti non salvati)
      else if (currentText == savedText) {
        valid = false;
      }
      // Se sono uguali → SBLOCCATO
    }

    if (type === 'linked') {
      const minAnswers = question.get('min_answers');
      const columns = question.get('columns');
      const currentColumn = columns.get(currentColumnIndex);

      if (!currentColumn) return false;

      const currentColumnId = currentColumn.get('order');

      //getting how many answers for the current column
      let answersGiven = 0;
      for (let answer of question.get('answers')) {
        const value = answer.get('value');
        if (value === currentColumnId) {
          answersGiven++;
        }
      }

      return answersGiven >= minAnswers;
    }

    return valid;
  }

  function isAllColumnsComplete() {
    if (type !== 'linked') return false;

    const minAnswers = question.get('min_answers');
    const columns = question.get('columns');

    const answersGivenAcc = new Map();
    for (let answer of question.get('answers')) {
      const value = answer.get('value');
      if (value !== undefined && value !== null) {
        const valueUntilNow = answersGivenAcc.get(value) || 0;
        answersGivenAcc.set(value, valueUntilNow + 1);
      }
    }

    for (const column of columns) {
      const columnId = column.get('order');
      const answersGiven = answersGivenAcc.get(columnId) || 0;
      if (answersGiven < minAnswers) {
        return false;
      }
    }

    return true;
  }

  function getInstructions(isSelfEval) {
    if (Array.isArray(jsonData)) {
      if (isSelfEval) {
        return <Trans id="questionnaire360.self_evaluation_text" />;
      }

      return <Trans id="questionnaire360.no_self_evaluation_text" />;
    }

    if (isSelfEval) {
      return typeof jsonData?.autoInstructions === 'string' ? (
        jsonData.autoInstructions
      ) : (
        <Trans id="questionnaire360.self_evaluation_text" />
      );
    }

    return typeof jsonData?.hetInstructions === 'string' ? (
      jsonData.hetInstructions
    ) : (
      <Trans id="questionnaire360.no_self_evaluation_text" />
    );
  }
  const isValid = getIsValid();
  const completedPercent = Math.round((count / total) * 100);

  const isSavingAnswer = loadingStates[1].includes(evaluationSavingState);
  const handleContinue = () => {
    // Salva sempre le risposte e passa alla pagina successiva
    onSave(question);
  };

  const handleColumnChange = newColumnIndex => {
    // Non più utilizzato - la navigazione avviene tramite currPage dal Container
    // Mantenuto per compatibilità con LinkedQuestion
    setCurrentColumnIndex(newColumnIndex);
  };

  // Calcola il numero totale di pagine includendo le colonne
  const getDisplayPageInfo = () => {
    if (type === 'linked') {
      const columns = question.get('columns');
      const totalColumns = columns ? columns.size : 1;
      return {
        currentPage: currPage,
        currentSubPage: currentColumnIndex + 1,
        totalSubPages: totalColumns,
        totalPage: totalPage,
      };
    }
    return {
      currentPage: currPage,
      currentSubPage: 1,
      totalSubPages: 1,
      totalPage: totalPage,
    };
  };

  const pageInfo = getDisplayPageInfo();
  return (
    <div ref={containerRef} className={s.Questionnaire360} tabIndex="-1">
      <Helmet>
        <title>
          {i18n._('questionnaire360.page_title', null, {
            defaults: 'Skillup Questionario 360 - Questionario',
          })}
        </title>
      </Helmet>
      <div className={s.TextContainer}>
        <h1 className="visually-hidden" aria-live="assertive" aria-atomic="true">
          <Trans
            id="question360.h1.title"
            components={[<>{props.evaluatedUserName || ''}</>, <>{currPage}</>, <>{totalPage}</>]}
          />
        </h1>
        {type === 'closed' && getInstructions(isSelfEval)}
        {type === 'linked' && question.get('label')}
      </div>
      <div className={s.ProgressBarContainer}>
        <div className={s.ProgressBar} style={{width: `${completedPercent}%`}} />
      </div>
      {type === 'closed' ? (
        <ClosedQuestion
          count={count}
          currPage={currPage}
          total={total}
          totalPage={totalPage}
          options={options}
          evaluation={evaluation}
          question={question}
          onSelect={handleSelectAnswer}
        />
      ) : type === 'linked' ? (
        <LinkedQuestion
          question={question}
          count={count}
          currPage={currPage}
          totalPage={totalPage}
          total={total}
          isSelfEval={isSelfEval}
          onSelect={handleSelectLinkedAnswer}
          currentColumnIndex={currentColumnIndex}
          onColumnChange={handleColumnChange}
        />
      ) : (
        <OpenQuestion
          count={count}
          currPage={currPage}
          total={total}
          totalPage={totalPage}
          evaluation={evaluation}
          question={question}
          onChange={handleAnswerInput}
        />
      )}{' '}
      <div className={s.ButtonContainer}>
        <FlatButton
          className={cls(s.FlatButton, isValid ? (isSavingAnswer ? s.Loading : s.Enabled) : '')}
          disabled={!isValid || isSavingAnswer}
          onClick={handleContinue}
        >
          {isSavingAnswer ? (
            <Spinner />
          ) : (
            <>
              <span className="visually-hidden">
                <Trans
                  id="questionnaire360.button.go_to_page_sr"
                  values={{currentPage: currPage + 1, totalPages: totalPage}}
                >
                  Vai alla pagina {currPage + 1} su {totalPage}
                </Trans>
              </span>
              <span aria-hidden="true">
                {type === 'linked' ? (
                  currentColumnIndex < pageInfo.totalSubPages - 1 ? (
                    <Trans id="questionnaire360.button.next_column">CONTINUA</Trans>
                  ) : currPage + 1 > totalPage ? (
                    <Trans id="questionnaire360.button.finish_questionnaire">
                      TERMINA IL QUESTIONARIO
                    </Trans>
                  ) : (
                    <Trans id="questionnaire360.button.continue">CONTINUA</Trans>
                  )
                ) : currPage + 1 > totalPage ? (
                  <Trans id="questionnaire360.button.finish_questionnaire">
                    TERMINA IL QUESTIONARIO
                  </Trans>
                ) : (
                  <Trans id="questionnaire360.button.continue">CONTINUA</Trans>
                )}
              </span>
            </>
          )}
        </FlatButton>{' '}
        <div className={s.Label}>
          {type === 'linked' ? (
            (() => {
              const minAnswers = question.get('min_answers');
              const columns = question.get('columns');
              const currentColumn = columns.get(currentColumnIndex);
              const currentColumnId = currentColumn.get('order');

              // Conta quante risposte sono state date per la colonna corrente
              let answersGiven = 0;
              for (let answer of question.get('answers')) {
                const value = answer.get('value');
                if (value === currentColumnId) {
                  answersGiven++;
                }
              }

              return (
                <Trans
                  id="questionnaire360.label.answer_column_progress"
                  values={{
                    minAnswers: minAnswers,
                    current: answersGiven,
                  }}
                >
                  Rispondi a {minAnswers} risposte per continuare ({answersGiven} su {minAnswers})
                </Trans>
              );
            })()
          ) : (
            <Trans id="questionnaire360.label.answer_to_continue">
              Rispondi alla domanda per continuare
            </Trans>
          )}
        </div>
      </div>
    </div>
  );
}

Questionnaire360.propTypes = {
  total: PropTypes.number,
  count: PropTypes.number,
  totalPage: PropTypes.number,
  currPage: PropTypes.number,
  isSelfEval: PropTypes.bool,
  options: PropTypes.array,
  evaluation: PropTypes.array,
  type: PropTypes.string,
  question: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
};
